import * as turf from '@turf/turf';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { GEOSERVER_URL } from 'src/environments/environment';
import { Feature, FeatureCollection, Geometry, Polygon } from 'geojson';
import { AlertValidationGeeImage } from '../../types';
import { Observable, retryWhen, take, delay, map } from 'rxjs';
import { AlertRefined } from '../../entities/alert-refined.entity.';
import { AlertRefinedPayload } from '../../interfaces/alert-refined-payload.interface';
import { ALERT_VALIDATION_LAYER, GEOSERVER_WORKSPACE } from '../../constants';
import { GEE_IMAGE_API_BASEURL } from 'src/environments/environment';
import API_URL from '../../utils/api-url-homolog';

@Injectable({
  providedIn: 'root'
})
export class AlertValidationService {

  constructor(
    private http: HttpClient
  ) { }

  async createAlert(alert: AlertRefinedPayload) {
    return this.http.post<any>(`${API_URL}/alert/validation/map/create`, alert)
      .pipe(map(response => response[0]))
      .toPromise();
  }

  async updateAlert(alert: AlertRefinedPayload) {
    return this.http.post<any>(`${API_URL}/alert/validation/map/updatealert`, alert)
      .pipe(map(response => response[0]))
      .toPromise();
  }

  async updateManyAlerts(params: any) {
    return this.http.post<any>(`${API_URL}/alert/validation/map/update`, params).toPromise();
  }

  async deleteAlerts(alertIds: number[]) {
    const payload = {
      "listId": alertIds,
      "userId":20 // #auth
    }
    return this.http.post<any>(`${API_URL}/alert/validation/map/delete`, payload)
      .pipe(map(response => response[0]))
      .toPromise();
  }

  async getAlertById(id: number): Promise<AlertRefined> {
    return this.http.post<AlertRefined[]>(`${API_URL}/alert/validation/map/select`, { alertId: id })
      .pipe(map(response => {
          return {
            ...response[0],
            feedbackId: response[0].feedbackId === 0 ? response[0].feedbackId = 99 : response[0].feedbackId,
            fieldNotesId: response[0].fieldNotesId === 0 ? response[0].fieldNotesId = 99 : response[0].fieldNotesId,
            statusId: response[0].statusId === 0 ? response[0].statusId = 3 : response[0].statusId
          }
        }
      ))
      .toPromise();
  }

  async getNextAlertId(id: number) {
    const payload = {
      service: 'WFS',
      version: '1.0.0',
      request: 'GetFeature',
      typeName: ALERT_VALIDATION_LAYER,
      maxFeatures: '1',
      outputFormat: 'application/json',
      CQL_FILTER: `ALERT_ID > ${id}`,
    };
    const httpParams = new HttpParams({
      fromObject: payload
    });
    const featuresCol = await this.http.get<FeatureCollection>(`${GEOSERVER_URL}/${GEOSERVER_WORKSPACE}/ows`, { params: httpParams }).toPromise();
    return featuresCol.features[0].properties['ALERT_ID'] as number;
    //return this.http.post<any>(`${API_URL}/alert/validation/nextid`, { alertId: id }).toPromise();
  }

  async getPreviousAlertId(id: number) {
    const payload = {
      service: 'WFS',
      version: '1.0.0',
      request: 'GetFeature',
      typeName: ALERT_VALIDATION_LAYER,
      maxFeatures: '1',
      sortBy: 'ALERT_ID D',
      outputFormat: 'application/json',
      CQL_FILTER: `ALERT_ID < ${id}`,
    };
    const httpParams = new HttpParams({
      fromObject: payload
    });
    const featuresCol = await this.http.get<FeatureCollection>(`${GEOSERVER_URL}/${GEOSERVER_WORKSPACE}/ows`, { params: httpParams }).toPromise();
    return featuresCol.features[0].properties['ALERT_ID'] as number;
    //return this.http.post<any>(`${API_URL}/alert/validation/previousid`, { alertId: id }).toPromise();
  }

  async getWfsAlertRefinedByLatLng(latLng: L.LatLng, buffer: number = 0, cqlFilter = ''): Promise<FeatureCollection> {
    const pointBuffer = turf.buffer(turf.point([latLng.lng, latLng.lat]), buffer, { units: 'kilometers' });
    const payload = {
      service: 'WFS',
      version: '1.0.0',
      request: 'GetFeature',
      typeName: ALERT_VALIDATION_LAYER,
      maxFeatures: '1',
      outputFormat: 'application/json',
      CQL_FILTER: `${cqlFilter != '' ? ('('+cqlFilter+') AND '):''} INTERSECTS("GEOM",POLYGON((${pointBuffer.geometry.coordinates[0].map(c => c[0] + ' ' + c[1]).join(', ')}))) AND ALERT_DELETED_AT is null`,
    };
    const httpParams = new HttpParams({
      fromObject: payload
    });
    const featuresCol = await this.http.get<FeatureCollection>(`${GEOSERVER_URL}/${GEOSERVER_WORKSPACE}/ows`, { params: httpParams }).toPromise();
    return featuresCol;
  }

  async getWfsFeatureById(id: number): Promise<Feature<Geometry>> {
    const payload = {
      service: 'WFS',
      version: '1.0.0',
      request: 'GetFeature',
      typeName: ALERT_VALIDATION_LAYER,
      outputFormat: 'application/json',
      CQL_FILTER: `ALERT_ID = ${id}`,
    };
    const httpParams = new HttpParams({
      fromObject: payload
    });
    const featuresCol = await this.http.get<FeatureCollection>(`${GEOSERVER_URL}/${GEOSERVER_WORKSPACE}/ows`, { params: httpParams }).toPromise();
    return featuresCol.features[0] as Feature<Geometry>;
  }

  async getWfsAlertRefinedByPolygon(polygon: Polygon, cqlFilter = ''): Promise<FeatureCollection> {
    const payload = {
      service: 'WFS',
      version: '1.0.0',
      request: 'GetFeature',
      typeName: ALERT_VALIDATION_LAYER,
      outputFormat: 'application/json',
      // CQL_FILTER: `INTERSECTS("GEOM",POLYGON((${polygon.coordinates[0].map(c => c[0] + ' ' + c[1]).join(', ')}))) AND ALERT_DELETED_AT is null`,
      CQL_FILTER: `${cqlFilter != '' ? ('('+cqlFilter+') AND '):''} INTERSECTS("GEOM",POLYGON((${polygon.coordinates[0].map(c => c[0] + ' ' + c[1]).join(', ')}))) AND ALERT_DELETED_AT is null`
    };
    const httpParams = new HttpParams({
      fromObject: payload
    });
    const featuresCol = await this.http.get<FeatureCollection>(`${GEOSERVER_URL}/${GEOSERVER_WORKSPACE}/ows`, { params: httpParams }).toPromise();
    return featuresCol;
  }

  async getWfsProdesByPolygon(polygon: Polygon, layer: string = null): Promise<FeatureCollection> {
    const payload = {
      service: 'WFS',
      version: '1.0.0',
      request: 'GetFeature',
      typeName: `${GEOSERVER_WORKSPACE}:GEO_MASCARA_DESMATAMENTO`,
      outputFormat: 'application/json',
      CQL_FILTER: `INTERSECTS("GEOM",POLYGON((${polygon.coordinates[0].map(c => c[0] + ' ' + c[1]).join(', ')})))`,
    };
    const httpParams = new HttpParams({
      fromObject: payload
    });
    const featuresCol = await this.http.get<FeatureCollection>(`${GEOSERVER_URL}/${GEOSERVER_WORKSPACE}/ows`, { params: httpParams }).toPromise();
    return featuresCol;
  }

  getAlertImages(params: {
    polygon: string,
    dateBefore: string,
    date: string,
    tempo: string,
    satellite: string,
    draw: boolean,
    bands: string,
    cloudCoverage: number,
    limit?: number // Optional parameter for limiting instances
  }): Observable<AlertValidationGeeImage[]> {

    const alertValidationImages$ = new Observable<AlertValidationGeeImage[]>((subscriber) => {
      ajax.post(`${GEE_IMAGE_API_BASEURL}/getImageList`, params)
        .then((result: any) => {
          const images: AlertValidationGeeImage[] = result.map((imgIds: any) => {
            return {
              imgID: imgIds,
              draw: false
            };
          });
          const imagesToProcess = params.limit ? images.slice(0, params.limit) : images;
          const resultToProcess = params.limit ? result.slice(0, params.limit) : result;
          subscriber.next(imagesToProcess);
          return { resultToProcess, imagesToProcess };
        })
        .then((response) => {
          return Promise.all(response.resultToProcess.map((imgId: string, index: number) => {
            return new Observable<AlertValidationGeeImage>((subscriber2) => {
              ajax.post(`${GEE_IMAGE_API_BASEURL}/getThumb`, {
                polygon: params.polygon,
                draw: false,
                image: imgId,
                order: index,
                satellite: params.satellite,
                bands: params.bands,
                cloudCoverage: params.cloudCoverage
              }).then(image => {
                response.imagesToProcess[index] = image as AlertValidationGeeImage;
                subscriber.next(response.imagesToProcess);
                subscriber2.next(response.imagesToProcess[index]);
              }).catch(error => {
                subscriber2.error(error);
              }).finally(() => {
                subscriber2.complete();
              });
            }).pipe(retryWhen(errors => errors.pipe(delay(2000), take(5)))).toPromise()
          }));
        })
        .then((response) => {
          subscriber.next(response);
        })
        .catch(error => {
          subscriber.error(error);
        })
        .finally(() => {
          subscriber.complete();
        });
    }).pipe(
      retryWhen(errors => errors.pipe(delay(2000), take(10)))
    );

    return alertValidationImages$;
  }

  getAlertImagesThumbs(alert: AlertRefined, _params: {
    satellite: string
    bands: string,
    cloudCoverage: number
  }): Promise<AlertValidationGeeImage[]> {

    const alertValidationImages$ = new Observable<AlertValidationGeeImage[]>((subscriber) => {

      const params = {
        polygon: alert.geometry,
        ..._params
      };

      new Promise(resolve => {
        const imageNames: any[] = [
          alert.beforeImg.name,
          alert.afterImg.name,
        ];
        resolve(imageNames)
      }).then((result: any) => {
        const images: AlertValidationGeeImage[] = result.map((imgIds: any) => {
          return {
            imgID: imgIds
          };
        });
        return { result, images };
      })
        .then((response) => {
          return Promise.all(response.result.map((imgId: string, index: number) => {
            return new Observable<AlertValidationGeeImage>((subscriber2) => {
              ajax.post(`${GEE_IMAGE_API_BASEURL}/getThumb`, {
                polygon: params.polygon,
                image: imgId,
                order: index,
                draw: false,
                satellite: params.satellite,
                bands: params.bands,
                cloudCoverage: params.cloudCoverage
              }).then(image => {
                response.images[index] = image as AlertValidationGeeImage;
                subscriber.next(response.images);
                subscriber2.next(response.images[index]);
                subscriber2.complete();
              }).catch(error => {
                subscriber2.error(error);
              })
            }).pipe(retryWhen(errors => errors.pipe(delay(2000), take(5)))).toPromise()
          }));
        })
        .then((response) => {
          subscriber.next(response);
          subscriber.complete();
        }).catch(error => {
          subscriber.error(error);
        })
    }).pipe(
      retryWhen(errors => errors.pipe(delay(2000), take(10)))
    );
    return alertValidationImages$.toPromise().then(result => result ? result.filter(r => r != undefined) : []);
  }

  getImagesThumbsByGeom(geom: Geometry, imageNames:string[],_params: {
    satellite: string
    bands: string,
    cloudCoverage: number
  }): Promise<AlertValidationGeeImage[]> {

    const alertValidationImages$ = new Observable<AlertValidationGeeImage[]>((subscriber) => {

      const params = {
        polygon: JSON.stringify(geom),
        ..._params
      };

      new Promise(resolve => {
        resolve(imageNames)
      }).then((result: any) => {
        const images: AlertValidationGeeImage[] = result.map((imgIds: any) => {
          return {
            imgID: imgIds
          };
        });
        return { result, images };
      })
        .then((response) => {
          return Promise.all(response.result.map((imgId: string, index: number) => {
            return new Observable<AlertValidationGeeImage>((subscriber2) => {
              ajax.post(`${GEE_IMAGE_API_BASEURL}/getThumb`, {
                polygon: params.polygon,
                image: imgId,
                order: index,
                draw:false,
                satellite: params.satellite,
                bands: params.bands,
                cloudCoverage: params.cloudCoverage
              }).then(image => {
                response.images[index] = image as AlertValidationGeeImage;
                subscriber.next(response.images);
                subscriber2.next(response.images[index]);
                subscriber2.complete();
              }).catch(error => {
                subscriber2.error(error);
              })
            }).pipe(retryWhen(errors => errors.pipe(delay(2000), take(5)))).toPromise()
          }));
        })
        .then((response) => {
          subscriber.next(response);
          subscriber.complete();
        }).catch(error => {
          subscriber.error(error);
        })
    }).pipe(
      retryWhen(errors => errors.pipe(delay(2000), take(10)))
    );
    return alertValidationImages$.toPromise().then(result => result ? result.filter(r => r != undefined) : []);
  }

  getStateGeom(): Promise<FeatureCollection> {
    return this.http.get<FeatureCollection>(`/assets/mt-geom.json`).toPromise();
  }

}

const ajax = {
  post: (url: string, params: any) => {
    return new Promise((resolve, reject) => {
      $.ajax({
        method: "POST",
        url: url,
        contentType: 'application/x-www-form-urlencoded; charset=utf-8',
        timeout: 20000,
        error: () => reject,
        data: params,
        dataType: 'json',
      }).done((response: any) => resolve(response))
        .fail((jqXHR: any, textStatus: any, errorThrown: any) => {
          reject(textStatus);
        })
    });
  }
}
